import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

import styles from './produtoformpage.module.css';

import { useProdutoService } from '../../services/produtoService';
import { URLS } from '../../constants/urls';

function ProdutoFormPage() {
    const { logout } = useAuth();
    const { showToast } = useToast();
    const { showLoader, hideLoader } = useLoader();
    const navigate = useNavigate();

    const { criarProduto, atualizarProduto, obterProdutoPorId } = useProdutoService();

    const { idProduto } = useParams();
    const isEditing = Boolean(idProduto);

    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        imagem: '',
        imagemFile: null,
        empresarial: '0',
    });

    const [imagemPreview, setImagemPreview] = useState(null);

    useEffect(() => {
        if (isEditing) {
            const carregarProduto = async () => {
                showLoader("Carregando dados do produto");

                try {
                    const response = await obterProdutoPorId(idProduto);
                    console.log('Resposta da API:', response);

                    if (response.success && response.data) {
                        const produto = response.data;

                        console.log('Dados do produto:', produto);

                        setFormData({
                            nome: produto.nome || '',
                            descricao: produto.descricao || '',
                            imagem: produto.imagem || '',
                            imagemFile: null,
                            empresarial: String(produto.empresarial ?? '0'),
                        });

                        if (produto.imagem) {
                            const imgPath = produto.imagem.startsWith('http')
                                ? produto.imagem
                                : `${URLS.IMG_PATH}${produto.imagem}`;
                            setImagemPreview(imgPath);
                        }
                    } else {
                        console.warn('Erro ao carregar produto:', response.message);
                        showToast('Erro ao carregar produto: ' + response.message, 3000, 'error');
                        if (response.message?.toLowerCase().includes('token')) logout();
                    }
                } catch (err) {
                    console.error('Erro inesperado:', err);
                        showToast('Erro ao carregar produto', 3000, 'error');
                }

                hideLoader();
            };

            carregarProduto();
        }
    }, [idProduto, isEditing]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file && file.type.startsWith('image/')) {
            setFormData((prev) => ({ ...prev, imagemFile: file }));

            const reader = new FileReader();
            reader.onloadend = () => setImagemPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        showLoader("Enviando dados do produto");

        const form = new FormData();
        form.append('nome', formData.nome);
        form.append('descricao', formData.descricao);
        form.append('empresarial', formData.empresarial);

        if (formData.imagemFile) {
            form.append('imagem', formData.imagemFile);
        }

        if (isEditing) form.append('id', idProduto);

        const response = isEditing
            ? await atualizarProduto(form)
            : await criarProduto(form);

        if (response.success) {
            showToast(isEditing ? 'Produto editado com sucesso!' : 'Produto criado com sucesso!', 3000, 'success');
            navigate('/produtos');
        } else {
            showToast(isEditing ? 'Erro ao atualizar produto.' : 'Erro ao criar produto.', 3000, 'error');
            console.log(response.message);
            if (response.message?.toLowerCase().includes('token')) logout();
        }

        hideLoader();
    };

    const breadcrumbLabel = isEditing ? `${formData.nome || 'produto'}` : undefined;

    return (
        <div className={styles.ProdutoFormPage}>
            <div className={styles.containerTop}>
                <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idProduto, label: breadcrumbLabel }] : []} />
                <h1 className={styles.title}>
                    {isEditing ? 'Editar Produto' : 'Cadastro de Produto'}
                </h1>
                <FriendlyPhrase page={isEditing ? "atualizarProduto" : "adicionarProduto"} />
            </div>

            <form onSubmit={handleSubmit} className={styles.formulario}>
                <label>
                    Nome:
                    <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                    />
                </label>

                <label>
                    Descrição:
                    <textarea
                        name="descricao"
                        value={formData.descricao}
                        onChange={handleChange}
                        required
                    />
                </label>

                <label>
                    Imagem:
                    <div className={styles.fileInputWrapper}>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className={styles.fileInput}
                        />
                        <span className={styles.fileLabel}>Escolher imagem</span>
                    </div>
                </label>

                {imagemPreview && (
                    <img
                        src={imagemPreview}
                        alt="Pré-visualização da imagem"
                        className={styles.logoPreview}
                    />
                )}

                <label>
                    Empresarial:
                    <select
                        name="empresarial"
                        value={formData.empresarial}
                        onChange={handleChange}
                        className={styles.selectInput}
                        required
                    >
                        <option value="1">Sim</option>
                        <option value="0">Não</option>
                    </select>
                </label>

                <div className={styles.submitArea}>
                    <button type="submit" className={styles.submitButton}>
                        {isEditing ? 'Atualizar Produto' : 'Salvar Produto'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ProdutoFormPage;
